import { DetailedHTMLProps, HTMLAttributes } from "react";
import { ReactMarkdownProps } from "react-markdown/lib/complex-types";

import {
  CitationDetails,
  CitationType,
} from "@elation/api-client/src/stitch-api";

import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@src/shared/components/ui/hover-card";
import { cn } from "@src/shared/lib/utils";
import { usePatientCitations } from "@src/shared/providers/citationsProviders";

const CitationTypeToText: Record<CitationType, string> = {
  DischargeSummary: "Discharge",
  LabPanel: "Lab Panel",
  Vitals: "Vitals",
  OfficeVisit: "Office Visit",
};

type SupProps = Omit<
  DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>,
  "ref"
> &
  ReactMarkdownProps;

function getFootnoteIdentifier(
  node: ReactMarkdownProps["node"],
): string | undefined {
  const anchor = node?.children?.[0];
  if (anchor?.type !== "element" || anchor.tagName !== "a") return undefined;
  const href = (anchor.properties?.href as string) ?? "";
  const match = href.match(/#(?:user-content-)?fn-(.+?)(?:-|$)/);
  return match ? match[1] : undefined;
}

const LabelValue: React.FC<{
  label: string;
  value: string;
  emphasize?: boolean;
}> = ({ label, value, emphasize }) => (
  <VerticalFlexbox gap="none">
    <Text sizing="xs" fontWeight={500} appearance="subdued">
      {label}
    </Text>
    <Text sizing="sm" fontWeight={emphasize ? 600 : 400}>
      {value}
    </Text>
  </VerticalFlexbox>
);

const CITATION_TONE: Record<CitationType, string> = {
  DischargeSummary: "bg-destructive/10 text-destructive",
  LabPanel: "bg-accent/10 text-accent",
  Vitals: "bg-success/10 text-success",
  OfficeVisit: "bg-warning/10 text-warning",
};

const DischargeSummaryContent: React.FC<{ citation: CitationDetails }> = ({
  citation,
}) => (
  <VerticalFlexbox gap="sm">
    <LabelValue label="Facility" value={citation.value} emphasize />
    <LabelValue label="Date" value={citation.date} />
    <LabelValue label="Principal Diagnosis" value={citation.description} />
  </VerticalFlexbox>
);

const DefaultContent: React.FC<{ citation: CitationDetails }> = ({
  citation,
}) => (
  <VerticalFlexbox gap="sm">
    <LabelValue label="Date" value={citation.date} />
    <LabelValue label="Value" value={citation.value} emphasize />
    <LabelValue label="Description" value={citation.description} />
  </VerticalFlexbox>
);

export const Sup = ({ className, node, ...props }: SupProps) => {
  const { citationsByMarker } = usePatientCitations();
  const footnoteNumber = getFootnoteIdentifier(node);
  const citation = footnoteNumber
    ? citationsByMarker[Number(footnoteNumber)]
    : undefined;

  if (!citation) return null;

  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>
        <sup
          {...props}
          className={cn(
            "ml-0.5 inline-flex size-4 items-center justify-center rounded-full bg-accent/15 text-[10px] font-semibold text-accent",
            className,
          )}
        >
          {footnoteNumber}
        </sup>
      </HoverCardTrigger>
      <HoverCardContent side="bottom" align="start" className="w-80 p-0">
        <HorizontalFlexbox
          alignItems="center"
          justifyContent="space-between"
          gap="xs"
          className={cn(
            "rounded-t-md px-3 py-2",
            CITATION_TONE[citation.citation_type],
          )}
        >
          <Text
            as="span"
            sizing="xs"
            fontWeight={600}
            className="uppercase tracking-wide"
          >
            {CitationTypeToText[citation.citation_type]}
          </Text>
          <Text as="span" sizing="xs" className="truncate">
            {citation.title}
          </Text>
        </HorizontalFlexbox>
        <VerticalFlexbox className="p-3">
          {citation.citation_type === CitationType.DischargeSummary ? (
            <DischargeSummaryContent citation={citation} />
          ) : (
            <DefaultContent citation={citation} />
          )}
        </VerticalFlexbox>
      </HoverCardContent>
    </HoverCard>
  );
};
